import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  Sequence,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  Zap,
  Activity,
  Cpu,
  ArrowRight,
  Flame,
  Volume2,
} from "lucide-react";
import { shatter } from "@remotion/effects/shatter";
import { TeenageEngineeringBot } from "./components/AntiSlopHardwareMascots";
import { DynamicSubtitle, SubtitleItem } from "./components/DynamicSubtitle";

// Timed Subtitles (4 Beats · 18s / 540 Frames)
const SUBTITLES: SubtitleItem[] = [
  {
    from: 0,
    to: 130,
    text: "Remotion baru aja drop update gede versi 4.0.539!",
    highlight: "versi 4.0.539",
  },
  {
    from: 130,
    to: 280,
    text: "Fitur paling gilanya: 3D Shatter Physics langsung dari kode!",
    highlight: "3D Shatter Physics",
  },
  {
    from: 280,
    to: 430,
    text: "Audio makin fleksibel pake preservePitch dan Studio jauh lebih enteng!",
    highlight: "preservePitch & Studio",
  },
  {
    from: 430,
    to: 540,
    text: "Eksplorasi video motion level pro lo sekarang bareng @zura.ailab!",
    highlight: "@zura.ailab",
  },
];

// FONT DECLARATIONS VIA STATICFILE
const fontFaces = `
@font-face {
  font-family: 'TekoDisplay';
  src: url('${staticFile("fonts/Teko-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
@font-face {
  font-family: 'BebasDisplay';
  src: url('${staticFile("fonts/BebasNeue-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'AntonDisplay';
  src: url('${staticFile("fonts/Anton-400.woff2")}') format('woff2');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'RajdhaniDisplay';
  src: url('${staticFile("fonts/Rajdhani-700.woff2")}') format('woff2');
  font-weight: 700;
  font-style: normal;
}
`;

export const ZuraRemotionUpdateShowcase: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Snappy Spring helper
  const sp = (delay: number, damping = 16, stiffness = 130) =>
    spring({
      frame: Math.max(0, frame - delay),
      fps,
      config: { damping, stiffness },
    });

  // Scene Transitions
  const s1Exit = interpolate(frame, [115, 130], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s1Opacity = 1 - s1Exit;
  const s1Y = interpolate(s1Exit, [0, 1], [0, -30]);

  const s2Exit = interpolate(frame, [265, 280], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s2Enter = interpolate(frame, [125, 140], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s2Opacity = s2Enter * (1 - s2Exit);
  const s2Y = interpolate(s2Exit, [0, 1], [0, -30]);

  const s3Exit = interpolate(frame, [415, 430], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s3Enter = interpolate(frame, [275, 290], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s3Opacity = s3Enter * (1 - s3Exit);
  const s3Y = interpolate(s3Exit, [0, 1], [0, -30]);

  const s4Enter = interpolate(frame, [425, 440], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const s4Opacity = s4Enter;

  // Shatter effect time parameter
  const shatterTime = Math.max(0, (frame - 165) / 24);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#F6F4EB", // Warm Paper
        color: "#1C1917",
        fontFamily: "'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      <style>{fontFaces}</style>

      {/* AUDIO: Ducked Background Music */}
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.09} loop />

      {/* SFX Sequences */}
      <Sequence from={10} durationInFrames={30}>
        <Audio src={staticFile("whoosh-short.mp3")} volume={0.4} />
      </Sequence>
      <Sequence from={165} durationInFrames={40}>
        <Audio src={staticFile("pop.mp3")} volume={0.6} />
      </Sequence>
      <Sequence from={285} durationInFrames={30}>
        <Audio src={staticFile("click-soft.mp3")} volume={0.4} />
      </Sequence>
      <Sequence from={430} durationInFrames={40}>
        <Audio src={staticFile("whoosh-short.mp3")} volume={0.5} />
      </Sequence>

      {/* TACTILE DOT GRID */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(28, 25, 23, 0.08) 1.5px, transparent 1.5px)",
          backgroundSize: "32px 32px",
          pointerEvents: "none",
        }}
      />

      {/* SUBTLE BORDER VIGNETTE */}
      <div
        style={{
          position: "absolute",
          inset: 24,
          border: "2.5px dashed rgba(28, 25, 23, 0.16)",
          borderRadius: 40,
          pointerEvents: "none",
        }}
      />

      {/* TOP HEADER PILL (SAFE ZONE Y: 260px) */}
      <div
        style={{
          position: "absolute",
          top: 250,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 80,
          transform: `translateY(${interpolate(sp(0, 18, 120), [0, 1], [-20, 0])}px)`,
          opacity: sp(0, 18, 120),
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            backgroundColor: "#FFFFFF",
            padding: "12px 28px",
            borderRadius: 999,
            border: "3px solid #1C1917",
            boxShadow: "5px 5px 0px #1C1917",
          }}
        >
          <Zap size={24} color="#EA580C" strokeWidth={2.5} />
          <span
            style={{
              fontFamily: "'RajdhaniDisplay', sans-serif",
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: 2,
              color: "#1C1917",
              textTransform: "uppercase",
            }}
          >
            REMOTION ENGINE UPDATE · @ZURA.AILAB
          </span>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#EA580C",
            }}
          />
        </div>
      </div>

      {/* ========================================================= */}
      {/* EDITORIAL STAGE CANVAS (Y: 340px - 1420px, WIDTH: 880px)  */}
      {/* ========================================================= */}
      <div
        style={{
          position: "absolute",
          top: 340,
          bottom: 500,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div
          style={{
            width: 880,
            height: "100%",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* ========================================================= */}
          {/* SCENE 1: THE ANNOUNCEMENT — REMOTION 4.0.539 (0 - 130)   */}
          {/* ========================================================= */}
          {frame < 135 && (
            <div
              style={{
                opacity: s1Opacity,
                transform: `translateY(${s1Y}px)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 24,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center", width: "100%" }}>
                <span
                  style={{
                    backgroundColor: "#EA580C",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 22,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 22px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 12,
                    border: "2px solid #1C1917",
                    boxShadow: "3px 3px 0px #1C1917",
                  }}
                >
                  ENGINE UPGRADE RELEASED
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 82,
                    lineHeight: 0.95,
                    letterSpacing: 1.5,
                    color: "#1C1917",
                    textTransform: "uppercase",
                  }}
                >
                  REMOTION v4.0.539
                </div>
                <div
                  style={{
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 32,
                    fontWeight: 700,
                    letterSpacing: 2,
                    color: "#2563EB",
                    textTransform: "uppercase",
                    marginTop: 8,
                  }}
                >
                  NEW EFFECTS · FAST TIMELINE · SOUND UPGRADE
                </div>
              </div>

              {/* OP-ZURA MASCOT HERO IN POINTING POSE */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 32,
                  marginTop: 10,
                }}
              >
                <div
                  style={{
                    transform: `scale(${interpolate(sp(5, 14, 110), [0, 1], [0.85, 1])})`,
                  }}
                >
                  <TeenageEngineeringBot size={340} pose="pointing" floating />
                </div>

                {/* Badge Card Beside Mascot */}
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "3.5px solid #1C1917",
                    borderRadius: 24,
                    padding: "24px 28px",
                    boxShadow: "6px 6px 0px #1C1917",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    maxWidth: 380,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      color: "#EA580C",
                      fontFamily: "'RajdhaniDisplay', sans-serif",
                      fontSize: 22,
                      fontWeight: 700,
                    }}
                  >
                    <Flame size={26} color="#EA580C" />
                    <span>FRESH FROM NPM</span>
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: "#1C1917",
                      lineHeight: 1.3,
                    }}
                  >
                    Seluruh monorepo di-update serentak ke versi 4.0.539 tanpa version mismatch!
                  </div>
                  <div
                    style={{
                      backgroundColor: "#FEF2F2",
                      border: "2px solid #DC2626",
                      borderRadius: 12,
                      padding: "8px 14px",
                      color: "#991B1B",
                      fontSize: 18,
                      fontWeight: 700,
                    }}
                  >
                    ⚡ Zero-Breakage Tested
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 2: THE 3D SHATTER EFFECT IN ACTION (125 - 280)      */}
          {/* ========================================================= */}
          {frame >= 120 && frame < 285 && (
            <div
              style={{
                opacity: s2Opacity,
                transform: `translateY(${s2Y}px)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 20,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    backgroundColor: "#2563EB",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 20px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 10,
                    border: "2px solid #1C1917",
                    boxShadow: "3px 3px 0px #1C1917",
                  }}
                >
                  NEW MODULE · @REMOTION/EFFECTS
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 74,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  3D SHATTER PHYSICS
                </div>
                <div
                  style={{
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 26,
                    fontWeight: 700,
                    color: "#DC2626",
                    letterSpacing: 1.5,
                    marginTop: 6,
                  }}
                >
                  SERPIHAN FISIKA 3D PECAH LANGSUNG DARI KODE!
                </div>
              </div>

              {/* STAGE ARENA: SHATTERED ELEMENT + OP-ZURA */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "4px solid #1C1917",
                  borderRadius: 28,
                  padding: "30px 40px",
                  boxShadow: "8px 8px 0px #1C1917",
                  width: "100%",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* 3D Physics Shattering Crystal Target */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      width: 280,
                      height: 280,
                      backgroundColor: "#1C1917",
                      borderRadius: 24,
                      border: "3px solid #1C1917",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Img
                      src={staticFile("diamond_3d_crystal.png")}
                      style={{ width: 230, height: 230 }}
                      effects={[
                        shatter({
                          time: shatterTime,
                          pattern: "bricks",
                          strength: 6.5,
                          gravity: 3.8,
                          extrusionDepth: 0.08,
                        }),
                      ]}
                    />

                    {/* Impact Stamp at trigger frame */}
                    {frame >= 165 && frame < 195 && (
                      <div
                        style={{
                          position: "absolute",
                          backgroundColor: "#DC2626",
                          color: "#FFFFFF",
                          padding: "6px 14px",
                          borderRadius: 8,
                          fontFamily: "'AntonDisplay', sans-serif",
                          fontSize: 24,
                          letterSpacing: 2,
                          transform: "rotate(-12deg) scale(1.1)",
                          boxShadow: "0 0 20px rgba(220, 38, 38, 0.6)",
                        }}
                      >
                        IMPACT!
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      fontFamily: "'RajdhaniDisplay', sans-serif",
                      fontSize: 22,
                      fontWeight: 700,
                      color: "#1C1917",
                    }}
                  >
                    {frame < 165 ? "🟡 SOLID STATE (T = 0s)" : "💥 SHATTERED (T > 0s)"}
                  </div>
                </div>

                {/* OP-ZURA MASCOT IN THINKING / EXCITED POSE */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <TeenageEngineeringBot size={310} pose="thinking" floating />
                  <div
                    style={{
                      backgroundColor: "#EFF6FF",
                      border: "2px solid #2563EB",
                      borderRadius: 12,
                      padding: "8px 16px",
                      color: "#1D4ED8",
                      fontSize: 18,
                      fontWeight: 700,
                      marginTop: 6,
                    }}
                  >
                    OP-ZURA MONITORING
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 3: STUDIO & AUDIO PRESERVE PITCH (275 - 430)        */}
          {/* ========================================================= */}
          {frame >= 270 && frame < 435 && (
            <div
              style={{
                opacity: s3Opacity,
                transform: `translateY(${s3Y}px)`,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    backgroundColor: "#16A34A",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 20,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 20px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 10,
                    border: "2px solid #1C1917",
                    boxShadow: "3px 3px 0px #1C1917",
                  }}
                >
                  SYSTEM PERFORMANCE PILLARS
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 68,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  AUDIO & TIMELINE SPEED BOOST
                </div>
              </div>

              {/* SCENE 3 STAGE: 3 CARDS + OP-ZURA TYPING */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 24,
                  width: "100%",
                }}
              >
                {/* 3 Vertical Stacked Cards */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
                  {/* Pillar 1 */}
                  <div
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "3.5px solid #1C1917",
                      borderRadius: 20,
                      padding: "16px 20px",
                      boxShadow: "5px 5px 0px #1C1917",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 14,
                          backgroundColor: "#FEF3C7",
                          border: "2.5px solid #D97706",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Volume2 size={26} color="#D97706" />
                      </div>
                      <div>
                        <div style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 22, fontWeight: 700, color: "#1C1917" }}>
                          preservePitch Audio Engine
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: "#4B5563" }}>
                          Audio cepat tanpa suara cempreng/melar.
                        </div>
                      </div>
                    </div>
                    <span style={{ backgroundColor: "#16A34A", color: "#FFFFFF", fontSize: 15, fontWeight: 800, padding: "5px 12px", borderRadius: 8 }}>
                      UPGRADED
                    </span>
                  </div>

                  {/* Pillar 2 */}
                  <div
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "3.5px solid #1C1917",
                      borderRadius: 20,
                      padding: "16px 20px",
                      boxShadow: "5px 5px 0px #1C1917",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 14,
                          backgroundColor: "#EFF6FF",
                          border: "2.5px solid #2563EB",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Activity size={26} color="#2563EB" />
                      </div>
                      <div>
                        <div style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 22, fontWeight: 700, color: "#1C1917" }}>
                          Timeline Persisten & Caching Math
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: "#4B5563" }}>
                          Waveform & spring() di-cache; reload instan!
                        </div>
                      </div>
                    </div>
                    <span style={{ backgroundColor: "#2563EB", color: "#FFFFFF", fontSize: 15, fontWeight: 800, padding: "5px 12px", borderRadius: 8 }}>
                      2X FASTER
                    </span>
                  </div>

                  {/* Pillar 3 */}
                  <div
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "3.5px solid #1C1917",
                      borderRadius: 20,
                      padding: "16px 20px",
                      boxShadow: "5px 5px 0px #1C1917",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 14,
                          backgroundColor: "#F0FDF4",
                          border: "2.5px solid #16A34A",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Cpu size={26} color="#16A34A" />
                      </div>
                      <div>
                        <div style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 22, fontWeight: 700, color: "#1C1917" }}>
                          Reverse Proxy HTTPS Studio Fixed
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: "#4B5563" }}>
                          Domain studio.kyuidn.cloud kebal API reject.
                        </div>
                      </div>
                    </div>
                    <span style={{ backgroundColor: "#EA580C", color: "#FFFFFF", fontSize: 15, fontWeight: 800, padding: "5px 12px", borderRadius: 8 }}>
                      RESOLVED
                    </span>
                  </div>
                </div>

                {/* OP-ZURA MASCOT IN TYPING POSE */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flexShrink: 0,
                    width: 270,
                  }}
                >
                  <TeenageEngineeringBot size={270} pose="typing" floating />
                  <div
                    style={{
                      backgroundColor: "#FEF3C7",
                      border: "2px solid #D97706",
                      borderRadius: 12,
                      padding: "6px 14px",
                      color: "#B45309",
                      fontSize: 16,
                      fontWeight: 800,
                      marginTop: 4,
                    }}
                  >
                    ⚡ OP-ZURA COMPILES
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCENE 4: OUTRO & @ZURA.AILAB BRAND CTA (425 - 540)        */}
          {/* ========================================================= */}
          {frame >= 420 && (
            <div
              style={{
                opacity: s4Opacity,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 24,
                width: "100%",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    backgroundColor: "#EA580C",
                    color: "#FFFFFF",
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 22,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: "6px 24px",
                    borderRadius: 999,
                    display: "inline-block",
                    marginBottom: 12,
                    border: "2px solid #1C1917",
                    boxShadow: "3px 3px 0px #1C1917",
                  }}
                >
                  THE FUTURE OF PROGRAMMATIC VIDEO
                </span>
                <div
                  style={{
                    fontFamily: "'AntonDisplay', sans-serif",
                    fontSize: 78,
                    lineHeight: 0.95,
                    color: "#1C1917",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  MOTION DESIGN DARI KODE
                </div>
                <div
                  style={{
                    fontFamily: "'RajdhaniDisplay', sans-serif",
                    fontSize: 28,
                    fontWeight: 700,
                    color: "#4B5563",
                    letterSpacing: 2,
                    marginTop: 8,
                  }}
                >
                  TANPA KLIK MANUAL · 100% PROGRAMMATIC
                </div>
              </div>

              {/* OUTRO HERO: OP-ZURA IN COFFEE CHILL POSE */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 36,
                }}
              >
                <div
                  style={{
                    transform: `scale(${interpolate(sp(425, 14, 110), [0, 1], [0.85, 1])})`,
                  }}
                >
                  <TeenageEngineeringBot size={340} pose="coffee" floating />
                </div>

                {/* Big Outro Card */}
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "4px solid #1C1917",
                    borderRadius: 24,
                    padding: "28px 32px",
                    boxShadow: "8px 8px 0px #1C1917",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    maxWidth: 380,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div
                      style={{
                        width: 58,
                        height: 58,
                        borderRadius: 16,
                        backgroundColor: "#EA580C",
                        color: "#FFFFFF",
                        fontFamily: "'AntonDisplay', sans-serif",
                        fontSize: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "3px solid #1C1917",
                      }}
                    >
                      Z
                    </div>
                    <div>
                      <div style={{ fontFamily: "'AntonDisplay', sans-serif", fontSize: 32, color: "#1C1917" }}>
                        ZURA • AI LAB
                      </div>
                      <div style={{ fontFamily: "'RajdhaniDisplay', sans-serif", fontSize: 20, fontWeight: 700, color: "#EA580C" }}>
                        @ZURA.AILAB
                      </div>
                    </div>
                  </div>

                  <div style={{ fontSize: 20, fontWeight: 600, color: "#374151", lineHeight: 1.35 }}>
                    Ikuti eksperimen rekayasa AI agent & sistem motion otomatis berikutnya di TikTok.
                  </div>

                  <div
                    style={{
                      backgroundColor: "#1C1917",
                      color: "#FFFFFF",
                      padding: "12px 20px",
                      borderRadius: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontFamily: "'RajdhaniDisplay', sans-serif",
                      fontSize: 22,
                      fontWeight: 700,
                      letterSpacing: 1,
                    }}
                  >
                    <span>FOLLOW SEKARANG</span>
                    <ArrowRight size={22} color="#EA580C" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DYNAMIC TIMED SUBTITLE (ANCHORED AT BOTTOM: 470px / SAFE ZONE Y: 1450px) */}
      <DynamicSubtitle
        frame={frame}
        subtitles={SUBTITLES}
        bottom={470}
        fontSize={32}
        highlightColor="#F59E0B"
        backgroundColor="rgba(28, 25, 23, 0.94)"
      />
    </AbsoluteFill>
  );
};
